import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useToast } from '../../components/shared/Toast';
import { MdCancel, MdSave } from 'react-icons/md';
import { cn } from '../../lib/utils';
import { apiFetch } from '../../utils/api';

// API URL Env
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:3000');

interface License {
  _id: string;
  licenseKey: string;
  licenseType: string; // 'customer' | 'vendor' stored as organizationType
  status: string;
  organizationId: string | { _id: string; name: string };
  expiryDate: string; // ISO string
  maxUsers: number;
  maxVessels?: number;
  maxItems?: number;
  currentUsage?: {
    users: number;
    vessels: number;
    items: number;
    employees: number;
  };
}

interface Organization {
  _id: string;
  name: string;
  type: string;
}

interface LicenseFormProps {
  license?: License | null;
  organizationId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function LicenseForm({ license, organizationId, onSuccess, onCancel }: LicenseFormProps) {
  const { showToast } = useToast();
  const isEditMode = !!license;

  // Form State
  const [formData, setFormData] = useState({
    organizationId: '',
    licenseType: 'customer',
    expiryDate: '',
    maxUsers: 10,
    maxVessels: 0,
    maxItems: 0,
    status: 'active'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch Organizations for Create Mode
  const { data: organizations = [] } = useQuery({
    queryKey: ['tech-organizations-list'],
    queryFn: async () => {
      const response = await apiFetch(`/api/v1/tech/organizations`);
      if (!response.ok) return [];
      const result = await response.json();
      return result.data as Organization[];
    },
    enabled: !isEditMode, // Only fetch for create mode
    staleTime: 60000
  });

  // Initialize Form Data
  useEffect(() => {
    if (license) {
      const orgId = typeof license.organizationId === 'object'
        ? license.organizationId._id
        : license.organizationId;

      setFormData({
        organizationId: orgId || '',
        licenseType: license.licenseType || 'customer',
        expiryDate: license.expiryDate ? new Date(license.expiryDate).toISOString().split('T')[0] : '',
        maxUsers: license.maxUsers || 10,
        maxVessels: license.maxVessels || 0,
        maxItems: license.maxItems || 0,
        status: license.status || 'active'
      });
    } else {
      // Create Mode Defaults
      const defaultExpiry = new Date();
      defaultExpiry.setFullYear(defaultExpiry.getFullYear() + 1);

      setFormData({
        organizationId: organizationId || '',
        licenseType: 'customer',
        expiryDate: defaultExpiry.toISOString().split('T')[0],
        maxUsers: 10,
        maxVessels: 5,
        maxItems: 1000,
        status: 'active'
      });
    }
  }, [license, organizationId]);

  // Handle Organization Selection (Auto-set type)
  const handleOrgChange = (orgId: string) => {
    const org = organizations.find(o => o._id === orgId);
    setFormData(prev => ({
      ...prev,
      organizationId: orgId,
      licenseType: org?.type === 'vendor' ? 'vendor' : 'customer'
    }));
  };

  // Downgrade Validation
  const validateDowngrade = () => {
    if (!license?.currentUsage) return true; // No usage data to validate against

    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (formData.maxUsers < license.currentUsage.users) {
      newErrors.maxUsers = `Cannot reduce limit below current usage (${license.currentUsage.users})`;
      isValid = false;
    }

    if (formData.licenseType === 'customer' && formData.maxVessels < license.currentUsage.vessels) {
      newErrors.maxVessels = `Cannot reduce limit below current usage (${license.currentUsage.vessels})`;
      isValid = false;
    }

    if (formData.licenseType === 'vendor' && formData.maxItems < license.currentUsage.items) {
      newErrors.maxItems = `Cannot reduce limit below current usage (${license.currentUsage.items})`;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Mutation
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const url = isEditMode
        ? `/api/v1/tech/licenses/${license._id}`
        : `/api/v1/tech/licenses`;
      const method = isEditMode ? 'PATCH' : 'POST';

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to ${isEditMode ? 'update' : 'create'} license`);
      }
      return response.json();
    },
    onSuccess: () => {
      showToast(`License ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
      onSuccess();
    },
    onError: (error: Error) => {
      showToast(error.message, 'error');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateDowngrade()) return;

    if (isEditMode) {
      // Construct PATCH payload (only changed fields)
      const payload: any = {};

      if (license) {
        if (formData.expiryDate !== new Date(license.expiryDate).toISOString().split('T')[0]) {
          payload.expiryDate = formData.expiryDate;
        }
        if (formData.maxUsers !== license.maxUsers) payload.maxUsers = formData.maxUsers;
        if (formData.maxVessels !== license.maxVessels) payload.maxVessels = formData.maxVessels;
        if (formData.maxItems !== license.maxItems) payload.maxItems = formData.maxItems;
        if (formData.status !== license.status) payload.status = formData.status;
        if (formData.licenseType !== license.licenseType) payload.licenseType = formData.licenseType;
      }

      if (Object.keys(payload).length === 0) {
        showToast('No changes detected', 'info');
        return;
      }
      mutation.mutate(payload);
    } else {
      // Create: backend casts licenseType → PlanType; org kind comes from Organization lookup.
      mutation.mutate({
        organizationId: formData.organizationId,
        expiryDate: formData.expiryDate,
        licenseType: 'professional',
        maxUsers: formData.maxUsers,
        maxVessels: formData.maxVessels,
        maxItems: formData.maxItems
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {!isEditMode && (
        <div>
          <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
            Organization <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.organizationId}
            onChange={(e) => handleOrgChange(e.target.value)}
            required
            className={cn(
              "w-full px-4 py-2.5 border-2 rounded-lg bg-white dark:bg-[#0088cc]/18 text-[hsl(var(--foreground))] dark:text-white transition-all",
              errors.organizationId ? 'border-red-500' : 'border-[#0088cc]/25 dark:border-[#0088cc]/20'
            )}
          >
            <option value="">Select Organization</option>
            {organizations.map((org) => (
              <option key={org._id} value={org._id}>
                {org.name} ({org.type})
              </option>
            ))}
          </select>
        </div>
      )}

      {isEditMode && license && typeof license.organizationId === 'object' && (
        <div className="p-3 bg-[#0088cc]/5 dark:bg-[#0088cc]/18/50 rounded-lg border border-[#0088cc]/20 dark:border-[#0088cc]/20">
          <span className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-wide">Organization</span>
          <div className="font-semibold text-[hsl(var(--foreground))] dark:text-white">{(license.organizationId as any).name || 'Unknown'}</div>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
          Plan Type <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.licenseType}
          onChange={(e) => setFormData({ ...formData, licenseType: e.target.value })}
          required
          // Allow changing plan type in Edit mode as requested ("Update Plan")
          className="w-full px-4 py-2.5 border-2 border-[#0088cc]/25 dark:border-[#0088cc]/20 rounded-lg bg-white dark:bg-[#0088cc]/18 text-[hsl(var(--foreground))] dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="customer">Customer</option>
          <option value="vendor">Vendor</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
          Expiry Date <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          value={formData.expiryDate}
          onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
          required
          className="w-full px-4 py-2.5 border-2 border-[#0088cc]/25 dark:border-[#0088cc]/20 rounded-lg bg-white dark:bg-[#0088cc]/18 text-[hsl(var(--foreground))] dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
            Max Users <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            value={formData.maxUsers}
            onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value) || 0 })}
            required
            className={cn(
              "w-full px-4 py-2.5 border-2 rounded-lg bg-white dark:bg-[#0088cc]/18 text-[hsl(var(--foreground))] dark:text-white outline-none focus:ring-2 focus:ring-blue-500",
              errors.maxUsers ? "border-red-500 ring-2 ring-red-200" : "border-[#0088cc]/25 dark:border-[#0088cc]/20"
            )}
          />
          {errors.maxUsers && <p className="mt-1 text-xs text-red-600 font-semibold">{errors.maxUsers}</p>}
        </div>

        {formData.licenseType === 'customer' && (
          <div>
            <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
              Max Vessels
            </label>
            <input
              type="number"
              min="0"
              value={formData.maxVessels}
              onChange={(e) => setFormData({ ...formData, maxVessels: parseInt(e.target.value) || 0 })}
              className={cn(
                "w-full px-4 py-2.5 border-2 rounded-lg bg-white dark:bg-[#0088cc]/18 text-[hsl(var(--foreground))] dark:text-white outline-none focus:ring-2 focus:ring-blue-500",
                errors.maxVessels ? "border-red-500 ring-2 ring-red-200" : "border-[#0088cc]/25 dark:border-[#0088cc]/20"
              )}
            />
            {errors.maxVessels && <p className="mt-1 text-xs text-red-600 font-semibold">{errors.maxVessels}</p>}
          </div>
        )}

        {formData.licenseType === 'vendor' && (
          <div>
            <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">
              Max Items
            </label>
            <input
              type="number"
              min="0"
              value={formData.maxItems}
              onChange={(e) => setFormData({ ...formData, maxItems: parseInt(e.target.value) || 0 })}
              className={cn(
                "w-full px-4 py-2.5 border-2 rounded-lg bg-white dark:bg-[#0088cc]/18 text-[hsl(var(--foreground))] dark:text-white outline-none focus:ring-2 focus:ring-blue-500",
                errors.maxItems ? "border-red-500 ring-2 ring-red-200" : "border-[#0088cc]/25 dark:border-[#0088cc]/20"
              )}
            />
            {errors.maxItems && <p className="mt-1 text-xs text-red-600 font-semibold">{errors.maxItems}</p>}
          </div>
        )}
      </div>

      {isEditMode && (
        <div>
          <label className="block text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 mb-2">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-4 py-2.5 border-2 border-[#0088cc]/25 dark:border-[#0088cc]/20 rounded-lg bg-white dark:bg-[#0088cc]/18 text-[hsl(var(--foreground))] dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-[#0088cc]/20 dark:border-[#0088cc]/20">
        <button
          type="button"
          onClick={onCancel}
          disabled={mutation.isPending}
          className="px-4 py-2 text-[hsl(var(--foreground))] dark:text-[#0088cc]/45 bg-[#0088cc]/10 dark:bg-[#0088cc]/18 hover:bg-[#0088cc]/15 dark:hover:bg-[#0088cc]/20 rounded-lg font-semibold transition-colors disabled:opacity-50"
        >
          <div className="flex items-center gap-2">
            <MdCancel className="w-4 h-4" />
            <span>Cancel</span>
          </div>
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          <div className="flex items-center gap-2">
            <MdSave className="w-4 h-4" />
            <span>{mutation.isPending ? 'Saving...' : 'Save Changes'}</span>
          </div>
        </button>
      </div>
    </form>
  );
}
